import type { MessageDescriptor } from '@lingui/core';
import type { LucideIcon } from 'lucide-react';
import { z } from 'zod';

const SUBMISSION_STATUSES = ['pending', 'uploading', 'uploaded', 'failed', 'abandoned'] as const;
export type SubmissionStatus = (typeof SUBMISSION_STATUSES)[number];

export type SubmissionMediaType = 'video' | 'mcap';

export interface SubmissionWire {
  sessionId: string;
  uploaderId: string;
  contentBlake3: string;
  gcsObject: string;
  mediaType: SubmissionMediaType;
  originalFilename: string;
  declaredBytes: string;
  receivedBytes: string;
  nextPart: number;
  status: SubmissionStatus;
  failReason: string | null;
  projectId: string;
  gcsGeneration: string | null;
  resumableUri: string | null;
  abandonedAt: string | null;
  abandonReason: 'resumable_expired' | null;
  replacedBySessionId: string | null;
}

export type Submission = Omit<SubmissionWire, 'declaredBytes' | 'receivedBytes'> & {
  declaredBytes: number;
  receivedBytes: number;
};

export type RestartUploadAction = 'restarted' | 'existing_replacement' | 'completed';

export interface RestartUploadResponseWire {
  action: RestartUploadAction;
  previousSession: SubmissionWire;
  session: SubmissionWire;
}

export interface RestartUploadResponse {
  action: RestartUploadAction;
  previousSession: Submission;
  session: Submission;
}

interface SubmissionHistoryFields {
  createdAt: string | null;
  updatedAt: string | null;
  lastProgressAt: string | null;
  resumableExpiresAt: string | null;
}

type SubmissionHistoryItemWire = SubmissionWire & SubmissionHistoryFields;
type SubmissionHistoryItem = Submission & SubmissionHistoryFields;

export type TransferHistoryQueryBucket = 'active' | 'checking' | 'attention' | 'done';

interface TransferHistoryCounts {
  all: number;
  active: number;
  checking: number;
  attention: number;
  done: number;
}

export interface SubmissionsPage {
  items: SubmissionHistoryItem[];
  nextCursor: string | null;
  counts: TransferHistoryCounts;
}

export interface SubmissionsPageWire extends Omit<SubmissionsPage, 'items'> {
  items: SubmissionHistoryItemWire[];
}

export const DEDUP_PRECHECK_OUTCOMES = [
  'nearDuplicate',
  'noAdvisoryMatch',
  'inconclusive',
] as const;
export type DedupPrecheckOutcome = (typeof DEDUP_PRECHECK_OUTCOMES)[number];

const SOURCE_IMPORT_STATES = [
  'pending',
  'probing',
  'pdq_check',
  'transferring',
  'transferred',
  'rejected',
  'failed',
] as const;
export type SourceImportState = (typeof SOURCE_IMPORT_STATES)[number];

const REJECT_KINDS = ['quality', 'exact_duplicate', 'near_duplicate', 'prefix_duplicate'] as const;
export type RejectKind = (typeof REJECT_KINDS)[number];

const progressCountSchema = z.number().int().nonnegative();
export const sourceImportProgressSchema = z.union([
  z.object({ fetchedBytes: progressCountSchema, totalBytes: progressCountSchema }).strict(),
  z.object({ sampledFrames: progressCountSchema, plannedFrames: progressCountSchema }).strict(),
  z.object({ copiedBytes: progressCountSchema, totalBytes: progressCountSchema }).strict(),
]);

export type SourceImportProgress = z.infer<typeof sourceImportProgressSchema>;

export interface SourceImportWire {
  importId: string;
  projectId: string;
  sourceUrl: string;
  filename: string;
  mediaType: 'video' | 'mcap';
  gcsObject: string;
  expectedBytes: number | null;
  contentBlake3: string | null;
  state: SourceImportState;
  rejectReason: string | null;
  rejectKind: RejectKind | null;
  attempts: number | null;
  maxAttempts: number;
}

export type SourceImport = Omit<SourceImportWire, 'attempts'> & { attempts: number };

interface SourceImportDetailFields {
  progress: SourceImportProgress | null;
  updatedAt: string;
}

export type SourceImportDetail = SourceImport & SourceImportDetailFields;

interface SourceImportHistoryFields {
  createdAt: string;
  updatedAt: string;
}

type SourceImportHistoryItemWire = SourceImportWire & SourceImportHistoryFields;
type SourceImportHistoryItem = SourceImport & SourceImportHistoryFields;

export interface SourceImportsPage {
  items: SourceImportHistoryItem[];
  nextCursor: string | null;
  counts: TransferHistoryCounts;
}

export interface SourceImportsPageWire extends Omit<SourceImportsPage, 'items'> {
  items: SourceImportHistoryItemWire[];
}

export interface GcsFolderScanItem {
  sourceUrl: string;
  relativePath: string;
  generation: string;
  sizeBytes: number;
}

export interface GcsFolderScanResponse {
  items: GcsFolderScanItem[];
}

export const GCS_FOLDER_IMPORT_DISPOSITIONS = [
  'created',
  'skipped_existing',
  'source_changed',
  'invalid_media',
  'source_missing',
  'source_forbidden',
  'temporarily_unavailable',
] as const;
export type GcsFolderImportDisposition = (typeof GCS_FOLDER_IMPORT_DISPOSITIONS)[number];

interface GcsFolderBatchItemBase {
  sourceUrl: string;
  ordinal: number;
  generation: string;
  sizeBytes: number;
  reasonCode: string | null;
}

type GcsFolderBatchItem =
  | (GcsFolderBatchItemBase & { disposition: 'created'; importId: string })
  | (GcsFolderBatchItemBase & {
      disposition: Exclude<GcsFolderImportDisposition, 'created'>;
      importId: string | null;
    });

export interface GcsFolderBatchResponse {
  batchId: string;
  items: GcsFolderBatchItem[];
}

const INTAKE_STATES = [
  'queued',
  'probing',
  'dedup_check',
  'pii_gate',
  'accepted',
  'rejected',
  'failed',
] as const;
export type IntakeState = (typeof INTAKE_STATES)[number];

export interface IntakeJob {
  intakeId: string;
  sourceId: string;
  contentBlake3: string;
  state: IntakeState;
  assetId: string | null;
  rejectReason: string | null;
  rejectKind: RejectKind | null;
  attempts: number;
  probeBlob: string | null;
}

const INTAKE_TERMINAL_STATES = ['accepted', 'rejected', 'failed'] as const;
export function isIntakeTerminal(state: IntakeState): boolean {
  return (INTAKE_TERMINAL_STATES as readonly string[]).includes(state);
}

export interface SourceImportStatusItem {
  importId: string;
  transferState: string;
  mcapStatus: string | null;
  intakeState: string | null;
  failure: string | null;
  failureCode?: string | null;
  progress?: unknown;
  assetId?: string | null;
  rejectKind?: RejectKind | null;
}

/** Owner- and project-authorized original record attached to a duplicate-source create conflict. */
export interface PreviousSourceImport {
  importId: string;
  filename: string;
  projectId: string;
  relocatedByOperationId: string | null;
  transferState: string;
  mcapStatus: string | null;
  intakeState: string | null;
  failure: string | null;
}

export interface LocalSubmissionStatusItem {
  sessionId: string;
  submissionStatus: SubmissionStatus;
  mcapStatus: string | null;
  intakeState: string | null;
  failure: string | null;
  failureCode?: string | null;
  mcapCheckGeneration?: number | null;
  mcapTaskId?: string | null;
  mcapRecheckable?: boolean;
  assetId?: string | null;
  rejectKind?: RejectKind | null;
}

export const UPLOAD_CHANNELS = ['upload', 'import'] as const;
export type UploadChannel = (typeof UPLOAD_CHANNELS)[number];

/**
 * The unified per-item phase the UI renders. Byte phase (queued..uploaded/transferred) is driven by
 * the upload engine / import poll; intake phase (processing..terminal) by the intake-job poll.
 * `blocked-duplicate` = pre-check exact-dup (no bytes uploaded); `blocked-near-duplicate` = PDQ gate-2
 * near-dup (session created, but no bytes uploaded); `failed` = system/transient budget exhausted,
 * distinct from a content rejection (`rejected-duplicate` / `rejected-quality`).
 */
export const UPLOAD_PHASES = [
  'queued',
  'reattaching',
  'hashing',
  'prechecking',
  'blocked-duplicate',
  'blocked-near-duplicate',
  'creating',
  'uploading',
  'paused',
  'transferring',
  'completing',
  'processing',
  'accepted',
  'rejected-duplicate',
  'rejected-quality',
  'failed',
  'error',
  'cancelled',
] as const;
export type UploadPhase = (typeof UPLOAD_PHASES)[number];

export type ResumeUploadFailureCode =
  | 'not_upload_leader'
  | 'transfer_unavailable'
  | 'file_required';

export type ResumeUploadResult =
  | { ok: true; status: 'queued' | 'already_queued' }
  | { ok: false; code: ResumeUploadFailureCode };

const IMPORT_DISPLAY_STAGES = [
  'remote-quality',
  'remote-duplicate',
  'server-copy',
  'intake-processing',
  'mcap-processing',
] as const;
export type ImportDisplayStage = (typeof IMPORT_DISPLAY_STAGES)[number];

export interface UploadProgress {
  completed: number;
  total: number;
  unit: 'bytes' | 'frames';
}

export type LocalUploadRetryOperation =
  | 'quality_check'
  | 'resolve_submission'
  | 'begin_resumable'
  | 'fetch_upload_offset'
  | 'report_progress'
  | 'upload_chunk'
  | 'complete_submission';

/** Render-safe projection of a scheduled automatic retry. The underlying Error stays in the engine
 * and is never persisted or exposed to the UI. */
export interface LocalUploadRetryState {
  operation: LocalUploadRetryOperation;
  attempt: number;
  maxRetries: number;
  delayMs: number;
  waitingForNetwork: boolean;
}

export interface UploadTarget {
  organizationId: string;
  organizationName?: string;
  projectId: string;
  projectName?: string;
}

export type StartSourceImportOutcome = 'started' | 'duplicate';

export interface StartSourceImportResult {
  id: string;
  outcome: StartSourceImportOutcome;
}

export interface UploadTask {
  /** Missing on legacy tasks means direct. Never persist relay tickets. */
  uploadRoute?: import('./uploadRelay').LocalUploadRoute;
  /** Stable client id for the queue row. */
  id: string;
  /** Client creation time used to keep the newest transfer history visible. */
  createdAt?: number;
  channel: UploadChannel;
  /** Direct uploads share one queue while retaining the backend media contract. */
  mediaType?: 'video' | 'mcap';
  target: UploadTarget;
  /** Transfer-queue label shown before an asset exists (file name for uploads, source URL for imports). */
  displayName: string;
  phase: UploadPhase;
  /** Last durable bytes / total (direct upload: GCS-confirmed; import: heartbeat approximation).
   * Browser-local in-chunk progress temporarily overlays this value in the rendered row. */
  bytes: number;
  totalBytes: number;
  /** Instantaneous throughput (bytes/s), direct upload only. */
  speedBps?: number;
  /** The backend id: submission `sessionId` (upload) or `importId` (import); also the intake-job id. */
  backendId?: string;
  /** Normalized provider source retained for ambiguous-create recovery and durable display. */
  sourceUrl?: string;
  /** Original import returned only for an authorized duplicate-source conflict. */
  previousSourceImport?: PreviousSourceImport;
  contentBlake3?: string;
  /** Current selection matched the caller's unfinished submission and can continue without rehashing. */
  resumeAvailable?: true;
  /** Populated when the intake job reaches `accepted`. */
  assetId?: string;
  /** Last completed gate-2 advisory. Persisted so a resumed upload does not silently lose whether
   * it received complete negative evidence or degraded to authoritative intake. */
  dedupPrecheckOutcome?: DedupPrecheckOutcome;
  /** Human-readable reason for a rejected/failed/error phase. */
  reason?: string;
  /** Stable MCAP terminal failure class. System diagnostics are never stored here. */
  mcapFailureCode?: string;
  /** A permanent status-read rejection (for example HTTP 400), distinct from an upload failure. The
   * row offers status refresh/dismiss and must never route this error to whole-file Retry. */
  statusSyncError?: true;
  /** Stable backend rejection class. Exact/near/prefix are grouped as Duplicate in the UI. */
  rejectKind?: RejectKind;
  /** Remote source-import stage and stage-local telemetry; never reused across stages. */
  importStage?: ImportDisplayStage;
  progress?: UploadProgress;
  /** True only after a source import has handed off to a real intake job. */
  intakeStarted?: boolean;
  /** Non-terminal transient retry indicator, e.g. "2/100". */
  attemptLabel?: string;
  /** Bounded client-side retry currently waiting before replaying a resumable upload operation. */
  autoRetry?: LocalUploadRetryState;
  /** Total automatic retries scheduled during this in-memory upload run (analytics only). */
  autoRetryCount?: number;
  /** Current MCAP scanner generation and immutable task id, retained for an explicit recheck. */
  mcapCheckGeneration?: number;
  mcapTaskId?: string;
  /** Authoritative backend projection for whether another server-side MCAP scan may be requested. */
  mcapRecheckable?: boolean;
  /** Advisory local-file findings. Backend intake remains authoritative and repairs the canonical copy. */
  mcapDescriptorWarnings?: ReadonlyArray<{
    schemaName: string;
    trailingBytes: number;
  }>;
  /** Advisory local-preflight failure code (e.g. `invalid_footer`) when the bounded descriptor read
   * could not validate the file. Non-blocking: the upload proceeds and backend intake is authoritative. */
  mcapPreflightAdvisory?: string;
  /** Reused after an ambiguous HTTP failure so the backend can replay the same receipt. */
  mcapRecheckRequestId?: string;
  /**
   * Set when a local upload no longer has a usable in-memory `File`: either after page rehydration or
   * after a browser-progress stall is confirmed against GCS. The row offers "Reattach file" so the
   * user can re-pick the original file and continue from the backend offset. Cleared once reattached;
   * always undefined for imports (server-side, no local File).
   */
  needsReattach?: boolean;
  /** Authoritatively expired resumable capability; restart requires the original verified File. */
  needsRestart?: boolean;
}

// ── Read-only history view-model (the `/transfers` page projections) ───────────────────────────────

/**
 * The read-only history page projects the two native list shapes (`Submission`, `SourceImport`) onto
 * one channel-agnostic view-model. It is distinct from `UploadTask` on purpose: `UploadTask` is
 * the live, operable queue model driven by the upload engine and keyed on `UploadPhase`, whereas a
 * history entry may combine the by-uploader row with its batched Intake / MCAP Ingest lifecycle
 * projection. Nothing here invents a timestamp or org/project name absent from those contracts.
 */

/**
 * The soft-tint presentation tone used by `HistoryStatusBadge` through the `StatusBadge` shell.
 * `progress` is the only tone that spins its icon.
 */
export type StatusTone = 'neutral' | 'progress' | 'positive' | 'warn' | 'destructive';

/**
 * The honest, channel-aware status buckets a history row collapses to. The native by-uploader rows
 * first express byte-transfer results; an optional batched Intake / MCAP Ingest projection then
 * refines the same row into its processing or final outcome.
 */
export type HistoryBucket =
  | 'in-progress'
  | 'uploaded'
  | 'restarted'
  | 'transferred'
  | 'duplicate'
  | 'rejected'
  | 'failed';

/**
 * Shared presentation grouping for the unified uploads surface. This is deliberately separate from
 * `HistoryBucket` and `UploadPhase`: those preserve the native backend/local lifecycle, while
 * this smaller vocabulary only answers how the UI groups a row.
 */
export type UploadViewBucket = 'active' | 'checking' | 'attention' | 'done';
/** Checking remains visible in All but is deliberately not presented as an Active transfer. */
export type UploadViewFilter = 'all' | Exclude<UploadViewBucket, 'checking'>;

export interface UploadViewCounts {
  active: number;
  all: number;
  checking: number;
  attention: number;
  done: number;
}

/** Full-channel counts returned by the paginated history endpoints. */
export const uploadViewCountsSchema = z.object({
  active: z.number().int().nonnegative(),
  all: z.number().int().nonnegative(),
  checking: z.number().int().nonnegative(),
  attention: z.number().int().nonnegative(),
  done: z.number().int().nonnegative(),
});

export type UploadHealthStatus = 'checking' | 'active' | 'interrupted' | 'expired';

export interface UploadHealthSnapshot {
  status: UploadHealthStatus;
  checkedAt?: number;
}

/** The history filter strip uses the same presentation vocabulary as live upload tasks. */
export type HistoryFilter = UploadViewFilter;

/**
 * The collapsed-row status descriptor produced by the pure `lib/historyStatus` lookups. `label` is a
 * `MessageDescriptor` (from `` msg`…` ``) so a pure module can name user text without a React context;
 * the component resolves it with `i18n._()`.
 */
export interface HistoryStatusDescriptor {
  bucket: HistoryBucket;
  /** Explicit page/panel grouping. Never derive this from a raw backend string in a component. */
  viewBucket: UploadViewBucket;
  label: MessageDescriptor;
  tone: StatusTone;
  icon?: LucideIcon;
}

/** The durable identity needed to turn an interrupted history row back into a live upload. The
 * original file itself is deliberately absent and must be selected by the user again. */
export interface HistoryUploadResume {
  sessionId: string;
  contentBlake3: string;
  mediaType: 'video' | 'mcap';
  projectId: string;
  displayName: string;
  declaredBytes: number;
  receivedBytes: number;
}

/**
 * The channel-agnostic row the history list renders. `backendId` is the submission `sessionId` or the
 * `importId`. `intakeId` is present only for a completed video transfer, because pending/uploading
 * rows and MCAP transfers do not own an intake job. `reason` / `rejectKind` / `assetId` may come from
 * the batched post-upload lifecycle projection, so refreshed history can show the final outcome.
 */
export interface HistoryEntry {
  channel: UploadChannel;
  mediaType?: 'video' | 'mcap';
  backendId: string;
  /** Eligible post-transfer video intake job. Absence disables the intake detail query. */
  intakeId?: string;
  /** Final asset identity when the batch lifecycle projection has already resolved ingestion. */
  assetId?: string;
  displayName: string;
  projectId: string;
  /** Current display status. A live projection may temporarily override the server snapshot. */
  status: HistoryStatusDescriptor;
  /** Native server projection retained when a live row overrides {@link status}. */
  serverStatus?: HistoryStatusDescriptor;
  /** Browser-local lifecycle retained separately from the native server status vocabulary. */
  livePhase?: UploadPhase;
  reason?: string;
  previousSourceImport?: PreviousSourceImport;
  rejectKind?: RejectKind;
  /** Loaded progress snapshot. The history page refreshes this value every 30 seconds. */
  progress?: UploadProgress;
  uploadResume?: HistoryUploadResume;
  /** Present for a browser-local queue failure/interruption that is not faithfully represented by
   * the server history row. It lets the Transfers page expose recovery without treating a client
   * error as a backend submission verdict. */
  liveTaskId?: string;
  localAction?: 'reattach' | 'retry' | 'refresh-status';
  reattachMediaType?: 'video' | 'mcap';
  /** A pending/uploading server row needs SharedWorker liveness evidence before it is called active. */
  uploadHealthRequired?: boolean;
}
